import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getPageNumbers } from '../home.logic'

interface ProjectsPaginationProps {
  currentPage: number
  totalPages: number
  totalCount: number
  pageSize: number
  onPageChange: (page: number) => void
}

export function ProjectsPagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}: ProjectsPaginationProps) {
  const { t } = useTranslation()

  if (totalPages <= 1) return null

  const pageNumbers = getPageNumbers(currentPage, totalPages, 5)

  // Calculate the range being displayed
  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalCount)

  return (
    <div className="border-border bg-card flex items-center justify-between border-t px-4 py-2.5">
      {/* Left side: Showing X-Y of Z projects */}
      <div className="text-muted-foreground text-xs">
        {t('pagination.showing', { start: startItem, end: endItem, total: totalCount })}
      </div>

      {/* Right side: Pagination controls */}
      <div className="flex items-center gap-1.5">
        {/* Previous button */}
        <button
          onClick={() => {
            onPageChange(currentPage - 1)
          }}
          disabled={currentPage === 1}
          className={cn(
            'flex h-7 items-center gap-0.5 rounded border px-2 text-xs font-medium transition-colors',
            currentPage === 1
              ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
              : 'border-border bg-card text-primary hover:bg-primary hover:text-primary-foreground'
          )}
          aria-label={t('pagination.previous')}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span>{t('pagination.previous')}</span>
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => {
                onPageChange(page)
              }}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded border text-xs font-medium transition-colors',
                currentPage === page
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-primary hover:bg-muted'
              )}
              aria-label={t('pagination.page', { page })}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => {
            onPageChange(currentPage + 1)
          }}
          disabled={currentPage === totalPages}
          className={cn(
            'flex h-7 items-center gap-0.5 rounded border px-2 text-xs font-medium transition-colors',
            currentPage === totalPages
              ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
              : 'border-border bg-card text-primary hover:bg-primary hover:text-primary-foreground'
          )}
          aria-label={t('pagination.next')}
        >
          <span>{t('pagination.next')}</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
